
export default function InformationArchitecture() {
  const navTypes = [
    { title: 'Primary Navigation', description: 'Module-level access via persistent sidebar with role-based visibility', icon: 'ri-layout-left-line' },
    { title: 'Contextual Navigation', description: 'Dynamic top bar showing current project, ITP, and ITC context', icon: 'ri-layout-top-line' },
    { title: 'Breadcrumb Trails', description: 'Hierarchical path display for deep navigation and quick backtracking', icon: 'ri-arrow-right-double-line' }
  ];

  return (
    <section className="py-20" style={{ background: 'linear-gradient(180deg, #FEF7F6 0%, #FFFFFF 100%)' }}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#182423', color: 'white' }}>
            System Structure
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#182423' }}>
            Information Architecture
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Designing complex hierarchical structures that feel intuitive and navigable
          </p>
        </div>

        {/* Hierarchy Visualization */}
        <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 mb-12">
          <h3 className="text-xl font-bold text-gray-900 mb-8 text-center">Hierarchical Structure</h3>
          
          <div className="flex flex-col items-center gap-4">
            {/* Organization Level */}
            <div className="w-full max-w-md">
              <div className="rounded-2xl p-4 text-center text-white font-bold" style={{ backgroundColor: '#182423' }}>
                <i className="ri-building-line mr-2"></i>
                Organization
              </div>
            </div>
            
            <div className="w-px h-8" style={{ backgroundColor: '#F16051' }}></div>
            
            {/* Project Level */}
            <div className="w-full max-w-lg">
              <div className="rounded-2xl p-4 text-center text-white font-bold" style={{ backgroundColor: '#F16051' }}>
                <i className="ri-folder-chart-line mr-2"></i>
                Project
              </div>
            </div>
            
            <div className="w-px h-8" style={{ backgroundColor: '#182423' }}></div>
            
            {/* ITP Level */}
            <div className="w-full max-w-xl">
              <div className="rounded-2xl p-4 text-center font-bold border-2" style={{ borderColor: '#182423', color: '#182423' }}>
                <i className="ri-file-list-3-line mr-2"></i>
                ITP (Inspection and Test Plan)
              </div>
            </div>
            
            <div className="w-px h-8" style={{ backgroundColor: '#F16051' }}></div>
            
            {/* ITC Level */}
            <div className="w-full max-w-2xl">
              <div className="rounded-2xl p-4 text-center font-bold border-2" style={{ borderColor: '#F16051', color: '#F16051' }}>
                <i className="ri-checkbox-multiple-line mr-2"></i>
                ITC (Inspection and Test Checklist)
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-500 mt-8 text-sm">
            Each level requires distinct permissions, templates, and workflows while maintaining data consistency
          </p>
        </div>

        {/* Navigation Types */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {navTypes.map((nav, index) => (
            <div key={index} className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                style={{ backgroundColor: index % 2 === 0 ? '#F16051' : '#182423' }}
              >
                <i className={`${nav.icon} text-white text-2xl`}></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{nav.title}</h4>
              <p className="text-gray-600 text-sm">{nav.description}</p>
            </div>
          ))}
        </div>

        {/* Drill-Down Pattern */}
        <div className="bg-gradient-to-r from-[#182423] to-[#2a3d3b] rounded-3xl p-8 text-white">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#F16051' }}>
              <i className="ri-arrow-down-double-line text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold">Drill-Down Patterns</h3>
          </div>
          <p className="text-white/80 leading-relaxed">
            Users frequently need to navigate from high-level project views down to individual inspection checklists. We designed consistent drill-down patterns that maintain context while progressively revealing detail. Each level provides summary information and clear entry points to the next level, reducing the cognitive effort required to navigate complex hierarchies.
          </p>
        </div>
      </div>
    </section>
  );
}
